import { Spinner, Center } from '@sequencehq/core-components'

import { useFetchCustomer } from 'Customer/hooks/useFetchCustomer'
import { useFetchCustomerAliases } from 'Customer/hooks/useFetchCustomerAliases'
import { Customer, CustomerAlias, Integration } from 'modules/Customers/types'
import { createContext, PropsWithChildren, useContext, useMemo } from 'react'
import { useFetchIntegrations } from 'Customer/hooks/useFetchIntegrations.ts'
import { useQuery } from '@sequencehq/api'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { MINUTES } from '@sequencehq/utils/dates'

export type CustomerContextData = {
  customer: Customer
  customerPortalEnabled: boolean
  customerAliases: CustomerAlias[]
  taxRegistrations: DashboardApi20240730.TaxRegistration.TaxRegistration[]
  integrations: Integration[]
  refetchCustomer: () => Promise<void>
  refetchCustomerAliases: () => Promise<void>
}

const CustomerContext = createContext<CustomerContextData | undefined>(
  undefined
)

export const CustomerContextProvider = ({
  customerId,
  children
}: PropsWithChildren<{
  customerId: string
  alwaysRenderChildren?: boolean
}>) => {
  const { customerDetails, refetch: refetchCustomer } =
    useFetchCustomer(customerId)

  const { customerAliases, refetch: refetchCustomerAliases } =
    useFetchCustomerAliases(customerId)

  const merchantQuery = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount,
    undefined,
    { staleTime: 5 * MINUTES }
  )

  const taxRegistrationQuery = useQuery(
    dashboard20240730Client.getTaxRegistrations,
    {
      customerId
    },
    { staleTime: 5 * MINUTES }
  )

  const { integrations } = useFetchIntegrations()

  const context = useMemo(() => {
    if (customerDetails) {
      return {
        customer: customerDetails,
        customerPortalEnabled:
          merchantQuery.data?.customerPortalEnabled || false,
        customerAliases: customerAliases?.items ?? [],
        taxRegistrations: (taxRegistrationQuery?.data?.items ?? [])
          .slice()
          .reverse(),
        integrations: integrations ?? [],
        refetchCustomer,
        refetchCustomerAliases
      }
    }
  }, [
    customerDetails,
    merchantQuery.data,
    customerAliases?.items,
    taxRegistrationQuery.data,
    integrations,
    refetchCustomer,
    refetchCustomerAliases
  ])

  if (!context?.customer) {
    return (
      <Center height="100%" width="100%">
        <Spinner />
      </Center>
    )
  }

  return (
    <CustomerContext.Provider value={context}>
      {children}
    </CustomerContext.Provider>
  )
}

export const useCustomerContext = () => {
  const context = useContext(CustomerContext)
  if (!context) {
    throw new Error(
      'useCustomerContext must be used within a CustomerContextProvider'
    )
  }
  return context
}
